// Upload utility with proper reCAPTCHA token handling
// Each API call gets a fresh token to avoid single-use token issues

import { buildApiUrl } from '@/config/env';
import { createApiHeaders } from './user';

interface UploadConfig {
  maxDirectUploadSize: number; // 50MB
  chunkSize: number; // 50MB per chunk
  maxConcurrentChunks: number; // Limit concurrent uploads
  retryAttempts: number; // Number of retry attempts
  retryDelay: number; // Delay between retries in ms
}

interface UploadProgress {
  totalChunks: number;
  uploadedChunks: number;
  currentChunk: number;
  progress: number; // 0-100
  status: 'preparing' | 'uploading' | 'assembling' | 'processing' | 'completed' | 'error';
  error?: string;
}

interface ChunkInfo {
  chunkNumber: number;
  totalChunks: number;
  chunkSize: number;
  startByte: number;
  endByte: number;
  isLastChunk: boolean;
}

class UploadManagerWithRecaptcha {
  private config: UploadConfig = {
    maxDirectUploadSize: 50 * 1024 * 1024, // 50MB
    chunkSize: 50 * 1024 * 1024, // 🚀 KEPT ORIGINAL: 50MB chunks (unchanged)
    maxConcurrentChunks: 6, // 🚀 DOUBLED CONCURRENT UPLOADS: Desktop: 12, Mobile: 6 (was Desktop: 6, Mobile: 3)
    retryAttempts: 3,
    retryDelay: 1000,
  };

  private progressCallbacks: ((progress: UploadProgress) => void)[] = [];
  private abortController: AbortController | null = null;

  constructor(config?: Partial<UploadConfig>) {
    if (config) {
      this.config = { ...this.config, ...config };
    }
    
    // Set maxConcurrentChunks based on device type - 🚀 DOUBLED CONCURRENT UPLOADS
    const isMobile = this.isMobileDevice();
    this.config.maxConcurrentChunks = isMobile ? 6 : 12; // 🚀 DOUBLED: Mobile: 6 (was 3), Desktop: 12 (was 6)
    console.log(`🚀 UploadManagerWithRecaptcha - Device type: ${isMobile ? 'Mobile' : 'Desktop'}, Max concurrent chunks: ${this.config.maxConcurrentChunks}`);
  }

  // Detect if device is mobile
  private isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  // Adjust config for mobile devices - 🚀 DOUBLED CONCURRENT UPLOADS: Doubled concurrent limit, kept original chunk size
  private getMobileOptimizedConfig(): UploadConfig {
    if (this.isMobileDevice()) {
      return {
        ...this.config,
        maxConcurrentChunks: 4, // 🚀 DOUBLED: 4 (was 2) - Reduce concurrent uploads on mobile
        chunkSize: 25 * 1024 * 1024, // 🚀 KEPT ORIGINAL: Smaller chunks for mobile (25MB, unchanged)
        retryAttempts: 5, // More retries for mobile
        retryDelay: 2000, // Longer delay for mobile
      };
    }
    return this.config;
  }

  // Calculate chunks for file
  private calculateChunks(file: File): ChunkInfo[] {
    const config = this.getMobileOptimizedConfig();
    const totalChunks = Math.ceil(file.size / config.chunkSize);
    const chunks: ChunkInfo[] = [];

    for (let i = 0; i < totalChunks; i++) {
      const startByte = i * config.chunkSize;
      const endByte = Math.min(startByte + config.chunkSize, file.size);
      const isLastChunk = i === totalChunks - 1;

      chunks.push({
        chunkNumber: i,
        totalChunks,
        chunkSize: endByte - startByte,
        startByte,
        endByte,
        isLastChunk,
      });
    }

    return chunks;
  }

  // Upload file with appropriate strategy
  async uploadFile(file: File, videoId: string, executeRecaptcha: (action: string) => Promise<string>): Promise<{ videoId: string; jobId: string; message: string }> {
    this.abortController = new AbortController();
    
    try {
      // Determine upload strategy based on file size
      if (file.size <= this.config.maxDirectUploadSize) {
        return await this.uploadSmallFile(file, videoId, executeRecaptcha);
      } else {
        return await this.uploadLargeFile(file, videoId, executeRecaptcha);
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Upload cancelled by user');
      }
      throw error;
    }
  }

  // Upload small files directly (≤ 50MB)
  private async uploadSmallFile(file: File, videoId: string, executeRecaptcha: (action: string) => Promise<string>): Promise<{ videoId: string; jobId: string; message: string }> {
    this.updateProgress({
      totalChunks: 1,
      uploadedChunks: 0,
      currentChunk: 0,
      progress: 0,
      status: 'preparing',
    });

    try {
      // Get single presigned URL for direct upload
      const presignedUrls = await this.getPresignedUrls(videoId, file.name, file.size, file.size, executeRecaptcha);
      
      this.updateProgress({
        totalChunks: 1,
        uploadedChunks: 0,
        currentChunk: 0,
        progress: 10,
        status: 'uploading',
      });

      // Upload file directly
      await this.uploadChunkToS3(presignedUrls[0].presigned_url, file);

      this.updateProgress({
        totalChunks: 1,
        uploadedChunks: 1,
        currentChunk: 0,
        progress: 100,
        status: 'assembling',
      });

      // Complete upload and start processing
      const result = await this.completeVideoUpload(videoId, executeRecaptcha);
      console.log('📤 UploadManager - completeVideoUpload result:', result);
      
      this.updateProgress({
        totalChunks: 1,
        uploadedChunks: 1,
        currentChunk: 0,
        progress: 100,
        status: 'processing',
      });

      console.log('📤 UploadManager - returning result:', { videoId, jobId: result.job_id, message: result.message });
      return { videoId, jobId: result.job_id, message: result.message };

    } catch (error) {
      this.updateProgress({
        totalChunks: 1,
        uploadedChunks: 0,
        currentChunk: 0,
        progress: 0,
        status: 'error',
        error: error instanceof Error ? error.message : 'Upload failed',
      });
      throw error;
    }
  }

  // Upload large files in chunks (> 50MB)
  private async uploadLargeFile(file: File, videoId: string, executeRecaptcha: (action: string) => Promise<string>): Promise<{ videoId: string; jobId: string; message: string }> {
    const chunks = this.calculateChunks(file);
    const config = this.getMobileOptimizedConfig();

    this.updateProgress({
      totalChunks: chunks.length,
      uploadedChunks: 0,
      currentChunk: 0,
      progress: 0,
      status: 'preparing',
    });

    try {
      // Get presigned URLs for all chunks
      const presignedUrls = await this.getPresignedUrls(videoId, file.name, file.size, config.chunkSize, executeRecaptcha);

      this.updateProgress({
        totalChunks: chunks.length,
        uploadedChunks: 0,
        currentChunk: 0,
        progress: 10,
        status: 'uploading',
      });

      // Upload chunks with concurrency control
      const uploadedChunks: string[] = [];
      const uploadPromises: Promise<void>[] = [];
      let currentConcurrent = 0;

      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        const presignedUrl = presignedUrls[i].presigned_url;

        // Wait if we've reached max concurrent uploads
        while (currentConcurrent >= config.maxConcurrentChunks) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        currentConcurrent++;
        const uploadPromise = this.uploadChunkToS3(presignedUrl, file.slice(chunk.startByte, chunk.endByte))
          .then(() => {
            uploadedChunks.push(presignedUrl);
            this.updateProgress({
              totalChunks: chunks.length,
              uploadedChunks: uploadedChunks.length,
              currentChunk: i,
              progress: Math.round((uploadedChunks.length / chunks.length) * 80) + 10,
              status: 'uploading',
            });
            currentConcurrent--;
          })
          .catch(error => {
            currentConcurrent--;
            throw error;
          });

        uploadPromises.push(uploadPromise);
      }

      // Wait for all chunks to upload
      await Promise.all(uploadPromises);

      this.updateProgress({
        totalChunks: chunks.length,
        uploadedChunks: chunks.length,
        currentChunk: chunks.length - 1,
        progress: 90,
        status: 'assembling',
      });

      // Complete upload and start processing
      const result = await this.completeVideoUpload(videoId, executeRecaptcha);
      console.log('📤 UploadManager - completeVideoUpload result:', result);

      this.updateProgress({
        totalChunks: chunks.length,
        uploadedChunks: chunks.length,
        currentChunk: chunks.length - 1,
        progress: 100,
        status: 'processing',
      });

      return { videoId, jobId: result.job_id, message: result.message };

    } catch (error) {
      this.updateProgress({
        totalChunks: chunks.length,
        uploadedChunks: 0,
        currentChunk: 0,
        progress: 0,
        status: 'error',
        error: error instanceof Error ? error.message : 'Upload failed',
      });
      throw error;
    }
  }

  // Get presigned URLs from Cloudflare Worker
  private async getPresignedUrls(videoId: string, fileName: string, fileSize: number, chunkSize: number, executeRecaptcha: (action: string) => Promise<string>): Promise<any[]> {
    // Get fresh reCAPTCHA token for this API call
    let recaptchaToken: string;
    try {
      recaptchaToken = await executeRecaptcha('file_upload');
      console.log('✅ Fresh reCAPTCHA token obtained for presigned URLs');
    } catch (error) {
      console.error('❌ Failed to get reCAPTCHA token for presigned URLs:', error);
      throw new Error('reCAPTCHA verification failed');
    }
    
    const baseUrl = buildApiUrl('/api/s3/presigned-urls');
    
    const headers: HeadersInit = {
      ...createApiHeaders(),
      'X-Recaptcha-Token': recaptchaToken,
    };

    const response = await fetch(baseUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        video_id: videoId,
        file_name: fileName,
        file_size: fileSize,
        chunk_size: chunkSize,
      }),
    });

    if (!response.ok) {
      // Try to parse error response for detailed message
      let errorMessage = `Failed to get presigned URLs: ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.error) {
          errorMessage = errorData.error;
          alert(errorMessage);

        }
        if (errorData.job_in_progress && errorData.job_details) {
          errorMessage = `${errorData.error}\n\nJob in progress: ${errorData.job_details.type} (${errorData.job_details.status})\nVideo ID: ${errorData.job_details.videoId}`;
          // Show browser alert for job in progress error
        }
      } catch (e) {
        // If parsing fails, use default message
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }

  // Upload chunk to S3
  private async uploadChunkToS3(presignedUrl: string, chunk: Blob): Promise<void> {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: chunk,
      headers: {
        'Content-Type': 'video/mp4',
      },
      signal: this.abortController?.signal,
    });

    if (!response.ok) {
      throw new Error(`Failed to upload chunk: ${response.statusText}`);
    }
  }

  // Complete video upload
  private async completeVideoUpload(videoId: string, executeRecaptcha: (action: string) => Promise<string>): Promise<any> {
    // Get fresh reCAPTCHA token for this API call
    let recaptchaToken: string;
    try {
      recaptchaToken = await executeRecaptcha('video_processing');
      console.log('✅ Fresh reCAPTCHA token obtained for complete upload');
    } catch (error) {
      console.error('❌ Failed to get reCAPTCHA token for complete upload:', error);
      throw new Error('reCAPTCHA verification failed');
    }
    
    const baseUrl = buildApiUrl('/api/video/complete-upload');
    
    const headers: HeadersInit = {
      ...createApiHeaders(),
      'X-Recaptcha-Token': recaptchaToken,
    };

    const response = await fetch(baseUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        video_id: videoId,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to complete upload: ${response.statusText}`);
    }

    return response.json();
  }

  // Update progress and notify callbacks
  private updateProgress(progress: UploadProgress): void {
    this.progressCallbacks.forEach(callback => callback(progress));
  }

  // Add progress callback
  onProgress(callback: (progress: UploadProgress) => void): void {
    this.progressCallbacks.push(callback);
  }

  // Remove progress callback
  offProgress(callback: (progress: UploadProgress) => void): void {
    const index = this.progressCallbacks.indexOf(callback);
    if (index > -1) {
      this.progressCallbacks.splice(index, 1);
    }
  }

  // Abort upload
  abort(): void {
    this.abortController?.abort();
  }

  // Get upload strategy info
  getUploadStrategy(file: File): {
    strategy: 'direct' | 'chunked';
    chunks: number;
    chunkSize: number;
    isMobile: boolean;
  } {
    const isMobile = this.isMobileDevice();
    const config = this.getMobileOptimizedConfig();
    const strategy = file.size <= this.config.maxDirectUploadSize ? 'direct' : 'chunked';
    const chunks = strategy === 'chunked' ? Math.ceil(file.size / config.chunkSize) : 1;

    return {
      strategy,
      chunks,
      chunkSize: config.chunkSize,
      isMobile,
    };
  }
}

export default UploadManagerWithRecaptcha;
